import { useCallback, useEffect, useRef, useState } from "react";

/** Tiempo que se muestra "está escribiendo" sin recibir señales nuevas. */
const VISIBLE_MS = 4000;

/** Intervalo mínimo entre susurros propios, para no inundar el socket. */
const THROTTLE_MS = 2000;

/**
 * Aviso de "está escribiendo...".
 *
 * Viaja como evento de cliente (whisper) entre navegadores: no toca la base de
 * datos ni la API, porque es información que caduca en segundos y no tiene
 * sentido guardarla.
 */
export const useTypingIndicator = () => {
    const [typing, setTyping] = useState([]);
    const timers = useRef({});
    const lastSignal = useRef(0);

    // Los temporizadores pendientes deben morir con el componente: si no,
    // intentan actualizar estado de una sala que el usuario ya abandonó.
    useEffect(() => () => {
        Object.values(timers.current).forEach(clearTimeout);
        timers.current = {};
    }, []);

    /** Handler para los susurros entrantes. */
    const receive = useCallback((payload) => {
        const id = payload?.id;
        const name = payload?.name;
        if (!id || !name) return;

        setTyping((prev) => (
            prev.some((person) => person.id === id) ? prev : [...prev, { id, name }]
        ));

        clearTimeout(timers.current[id]);
        timers.current[id] = setTimeout(() => {
            delete timers.current[id];
            setTyping((prev) => prev.filter((person) => person.id !== id));
        }, VISIBLE_MS);
    }, []);

    /** Olvida a quien ya envió su mensaje, sin esperar a que caduque. */
    const forget = useCallback((id) => {
        clearTimeout(timers.current[id]);
        delete timers.current[id];
        setTyping((prev) => prev.filter((person) => person.id !== id));
    }, []);

    /** true como mucho una vez cada THROTTLE_MS: pulsar teclas no es un evento. */
    const shouldSignal = useCallback(() => {
        const now = Date.now();
        if (now - lastSignal.current < THROTTLE_MS) return false;
        lastSignal.current = now;
        return true;
    }, []);

    const reset = useCallback(() => {
        lastSignal.current = 0;
    }, []);

    return { typing, receive, forget, shouldSignal, reset };
};
